import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Users, MapPin, Phone, Mail, Calendar, Star } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompatibiliCandidati = ({ agenziaId }) => {
  const [candidatiCompatibili, setCandidatiCompatibili] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agenzia, setAgenzia] = useState(null);

  useEffect(() => {
    if (agenziaId) {
      loadCompatibiliCandidati();
    }
  }, [agenziaId]);

  const loadCompatibiliCandidati = async () => {
    try {
      setLoading(true);
      
      // Load agency details and candidates
      const [agenziaRes, candidatiRes] = await Promise.all([
        fetch(`${API}/agenzie/${agenziaId}`),
        fetch(`${API}/candidati`)
      ]);

      if (agenziaRes.ok && candidatiRes.ok) {
        const agenziaData = await agenziaRes.json();
        const candidatiData = await candidatiRes.json();
        
        setAgenzia(agenziaData);
        
        // Filter compatible candidates based on agency location and requirements
        const compatibili = candidatiData.filter(candidato => {
          // Basic compatibility checks - more flexible matching
          const agenziaZona = agenziaData.zona?.toLowerCase() || '';
          const candidatoZona = candidato.zona_interesse?.toLowerCase() || '';
          const candidatoCitta = candidato.citta?.toLowerCase() || '';
          
          // Check if candidate's preferred zone or city matches agency location
          const zonaCompatibile = 
            candidatoZona.includes('qualsiasi') ||
            candidatoZona === agenziaZona ||
            candidatoCitta === agenziaZona ||
            agenziaZona.includes(candidatoCitta) ||
            candidatoCitta.includes(agenziaZona.split(' ')[0]) || // Match city name from address
            candidatoZona.includes(agenziaZona.split(' ')[0]);
          
          // Include candidates in early pipeline stages
          const statoValido = ['Nuovo', 'Contattato', 'Modulo inviato', 'Form compilato', 'In attesa colloquio'].includes(candidato.stato_pipeline);
          
          return zonaCompatibile && statoValido;
        });
        
        setCandidatiCompatibili(compatibili);
      }
    } catch (error) {
      console.error("Errore nel caricamento candidati compatibili:", error);
      toast.error("Errore nel caricamento candidati compatibili");
    } finally {
      setLoading(false);
    }
  };

  const getCompatibilityScore = (candidato) => {
    let score = 0;
    
    const agenziaZona = agenzia?.zona?.toLowerCase() || '';
    const candidatoZona = candidato.zona_interesse?.toLowerCase() || '';
    const candidatoCitta = candidato.citta?.toLowerCase() || '';
    
    // Zone match (more flexible)
    if (candidatoZona.includes('qualsiasi')) score += 10;
    else if (candidatoZona === agenziaZona || candidatoCitta === agenziaZona) score += 30;
    else if (agenziaZona.includes(candidatoCitta) || candidatoCitta.includes(agenziaZona.split(' ')[0])) score += 25;
    else if (candidatoZona.includes(agenziaZona.split(' ')[0])) score += 20;
    
    // Age preference
    if (candidato.eta >= 25 && candidato.eta <= 45) score += 20;
    else if (candidato.eta >= 20 && candidato.eta <= 50) score += 15;
    
    // Pipeline stage
    if (candidato.stato_pipeline === 'Form compilato') score += 25;
    else if (candidato.stato_pipeline === 'Modulo inviato') score += 20;
    else if (candidato.stato_pipeline === 'In attesa colloquio') score += 18;
    else if (candidato.stato_pipeline === 'Contattato') score += 15;
    else if (candidato.stato_pipeline === 'Nuovo') score += 10;
    
    // Experience indicators
    if (candidato.esperienza_flag === true) score += 15;
    if (candidato.anni_esperienza > 0) score += Math.min(candidato.anni_esperienza, 10);
    
    // Use existing candidate score if available
    if (candidato.candidate_score) {
      score = Math.max(score, candidato.candidate_score * 0.8); // Use 80% of existing score as base
    }
    
    return Math.min(score, 100);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const assignCandidatoToAgenzia = async (candidatoId) => {
    try {
      // Simulate API call to assign candidate to agency
      toast.success("Candidato assegnato all'agenzia con successo");
      
      // Remove from compatible list
      setCandidatiCompatibili(prev => prev.filter(c => c.id !== candidatoId));
    } catch (error) {
      toast.error("Errore nell'assegnazione del candidato");
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Caricamento candidati compatibili...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!agenzia) {
    return (
      <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">Agenzia non trovata</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Agency Info Header */}
      <Card className="bg-blue-50/80 backdrop-blur-xl border-blue-200 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Users className="w-5 h-5" />
            Candidati Compatibili per {agenzia.nome_agenzia}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span><strong>Zona:</strong> {agenzia.zona}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span><strong>Posti disponibili:</strong> {agenzia.posti_disponibili}</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-blue-600" />
              <span><strong>Candidati trovati:</strong> {candidatiCompatibili.length}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compatible Candidates List */}
      {candidatiCompatibili.length === 0 ? (
        <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun candidato compatibile</h3>
            <p className="text-gray-600">
              Non ci sono candidati compatibili per questa agenzia al momento.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {candidatiCompatibili
            .sort((a, b) => getCompatibilityScore(b) - getCompatibilityScore(a))
            .map(candidato => {
              const score = getCompatibilityScore(candidato);
              return (
                <Card key={candidato.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-semibold text-gray-900 text-lg">
                            {candidato.nome} {candidato.cognome}
                          </h3>
                          <Badge className={getScoreColor(score)}>
                            {score}% compatibile
                          </Badge>
                          <Badge className="bg-blue-100 text-blue-700 border-0">
                            {candidato.stato_pipeline}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{candidato.eta ? `${candidato.eta} anni` : 'Età non specificata'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{candidato.zona_interesse || candidato.citta || 'Non specificata'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{candidato.telefono || 'Non disponibile'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span>{candidato.email || 'Non disponibile'}</span>
                          </div>
                        </div>

                        {(candidato.esperienza_flag || candidato.anni_esperienza > 0) && (
                          <div className="mt-3 flex gap-2">
                            {candidato.esperienza_flag && (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                Ha esperienza
                              </Badge>
                            )}
                            {candidato.anni_esperienza > 0 && (
                              <Badge variant="outline" className="text-blue-600 border-blue-300">
                                {candidato.anni_esperienza} anni esperienza
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => assignCandidatoToAgenzia(candidato.id)}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Assegna
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default CompatibiliCandidati;